<template>
  <div class="w-full flex items-center gap-2">
    <div class="flex-1 flex items-center justify-center gap-[10px]">
      <div
        v-for="(_, index) in code"
        :key="index"
        :class="[
          'otp-cell',
          {
            'otp-cell--password': isMasked,
            'otp-cell--disabled': disabled,
            'focus-white': focusBorderWhite,
          },
        ]"
        contenteditable="true"
        spellcheck="false"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        inputmode="numeric"
        aria-label="One-time code digit"
        :data-index="index"
        ref="cells"
        :disabled="disabled"
        @beforeinput="handleBeforeInput(index, $event as InputEvent)"
        @input="handleInput(index, $event)"
        @keydown="handleKeyDown(index, $event)"
        @focus="handleFocus(index)"
        @paste="handlePaste($event)"
        @copy="onBlockIfPassword"
        @cut="onBlockIfPassword"
        @contextmenu="onBlockIfPassword"
        @drop.prevent
        @dragstart.prevent
      ></div>
    </div>

    <!-- 仅在开启密码模式且允许显示按钮时展示小眼睛，切换"可见/不可见"（只影响显示，仍禁止粘贴） -->
    <button
      v-if="props.isPassword && props.showToggleButton"
      type="button"
      class="otp-eye-btn"
      :class="[props.btnPosition, { 'otp-eye-btn--disabled': disabled }]"
      :aria-label="showPassword ? '隐藏密码' : '显示密码'"
      :disabled="disabled"
      @click="toggleVisibility"
    >
      <!-- 简单的 SVG 图标：眼睛/闭眼 -->
      <svg
        v-if="!showPassword"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"
          stroke="currentColor"
          stroke-width="2"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
      </svg>
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path d="M3 3l18 18" stroke="currentColor" stroke-width="2" />
        <path
          d="M10.6 5.1A11 11 0 0 1 12 5c6.5 0 10 6 10 6a18.9 18.9 0 0 1-4.1 4.8M6.1 6.1A18.6 18.6 0 0 0 2 11s3.5 6 10 6c1.5 0 2.9-.3 4.2-.8"
          stroke="currentColor"
          stroke-width="2"
        />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'

const props = withDefaults(
  defineProps<{
    length?: number
    modelValue?: string
    isPassword?: boolean
    btnPosition?: string
    disabled?: boolean
    showToggleButton?: boolean
    focusBorderWhite?: boolean //选中时白色描边
  }>(),
  {
    btnPosition: 'right', // 默认值为'right'
    disabled: false,
    showToggleButton: true, // 默认显示切换按钮
    focusBorderWhite: false,
  },
)
const emit = defineEmits<{
  (e: 'submit', code: string): void
  (e: 'update:modelValue', code: string): void
}>()

// 解构时设置默认值

const codeLength = props.length || 6
const code = ref<string[]>(Array(codeLength).fill(''))
const currentIndex = ref(0)
const cells = ref<HTMLElement[]>([])

// 显示控制——仅在 isPassword 开启且未显示明文时进行遮罩
const showPassword = ref(false)
const isMasked = computed(() => !!props.isPassword && !showPassword.value)

// 将光标强制放到 contenteditable 的末尾
const placeCaretAtEnd = (el: HTMLElement) => {
  const range = document.createRange()
  const sel = window.getSelection()
  range.selectNodeContents(el)
  range.collapse(false) // false 表示末尾
  sel?.removeAllRanges()
  sel?.addRange(range)
}

// 根据“是否遮罩”来决定显示字符（• 或明文）
const displayChar = (ch: string) => (isMasked.value && ch ? '•' : ch)

// 把 code 显示同步到每个 cell（注意密码模式下显示 •）
const syncCellsFromCode = () => {
  cells.value.forEach((el, i) => {
    if (!el) return
    const want = code.value[i] || ''
    const show = displayChar(want)
    if (el.innerText !== show) el.innerText = show
  })
}

const blurAllCells = () => {
  cells.value.forEach((el) => el?.blur?.())
  const active = document.activeElement as HTMLElement | null
  active?.blur?.()
}

watch(
  code,
  () => {
    emit('update:modelValue', code.value.join(''))
    nextTick(syncCellsFromCode)
  },
  { deep: true },
)

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue == null) return
    const digits = newValue.replace(/\D/g, '').slice(0, codeLength).split('')
    code.value = [...digits, ...Array(codeLength - digits.length).fill('')]
    nextTick(syncCellsFromCode)
  },
  { immediate: true },
)

// 显隐切换仅影响显示；切换后需要重新同步显示为明文/•
const toggleVisibility = () => {
  // 如果禁用状态，阻止切换
  if (props.disabled) {
    return
  }

  showPassword.value = !showPassword.value
  nextTick(syncCellsFromCode)
}

// beforeinput 捕获"批量插入/替换"（含粘贴），比 input 更早
const handleBeforeInput = (index: number, e: InputEvent) => {
  // 如果禁用状态，阻止所有输入
  if (props.disabled) {
    e.preventDefault()
    return
  }

  const inputType = (e as any).inputType || ''
  const data = (e as any).data as string | null

  // 删除/历史操作交给浏览器处理
  if (inputType.startsWith('delete') || inputType.startsWith('history')) return

  // 检查是否已经填满，如果填满了且不是删除操作，则阻止输入
  const isFull = code.value.every((char) => char !== '')
  if (isFull && !inputType.startsWith('delete')) {
    e.preventDefault()
    nextTick(blurAllCells)
    return
  }

  if (props.isPassword) {
    // 密码模式——禁用粘贴和多字符，并只允许数字
    if (inputType === 'insertFromPaste' || (data && data.length > 1)) {
      e.preventDefault()
      return
    }
    if (data && /\D/.test(data)) {
      e.preventDefault()
      return
    }
  } else {
    // 非密码模式：多字符或非数字 -> 自行分发为粘贴流
    if ((data && data.length > 1) || (data && /\D/.test(data))) {
      e.preventDefault()
      const numbers = (data ?? '').replace(/\D/g, '')
      if (numbers) fillFrom(index, numbers)
      return
    }
  }
  // 单字符数字：交给 handleInput 收敛
}

// 单字符输入兜底——确保格子只保留一个数字，并在有值时移到下一格
const handleInput = (index: number, event: Event) => {
  // 如果禁用状态，阻止所有输入
  if (props.disabled) {
    return
  }

  const el = event.target as HTMLElement
  const ie = event as InputEvent

  // 检查是否已经填满，如果填满了则阻止输入
  const isFull = code.value.every((char) => char !== '')
  if (isFull) {
    ;(document.activeElement as HTMLElement | null)?.blur?.()
    return
  }
  // 优先用 InputEvent.data 获取真实输入，避免被 • 干扰；输入法或特殊情况兜底处理
  let text = ''
  if (ie && typeof (ie as any).data === 'string') {
    text = ((ie as any).data || '').replace(/\D/g, '').slice(-1)
  } else {
    // 遮罩时不能从 innerText 读值（会是 •），非遮罩可从 innerText 提取
    const base = isMasked.value ? '' : el.innerText || ''
    text = (base || '').replace(/\D/g, '').slice(-1)
  }

  code.value[index] = text
  el.innerText = displayChar(text)
  placeCaretAtEnd(el)

  if (text && index < codeLength - 1) {
    // 找到下一个空位置
    const nextEmptyIndex = code.value.findIndex((char, i) => i > index && char === '')
    const targetIndex = nextEmptyIndex === -1 ? index + 1 : nextEmptyIndex

    currentIndex.value = targetIndex
    nextTick(() => cells.value[targetIndex]?.focus())
  } else {
    checkComplete()
  }
}

// Backspace  —— 有值先删当前；无值再跳到前一格并删前一格
const handleKeyDown = (index: number, event: KeyboardEvent) => {
  // 如果禁用状态，阻止所有键盘操作
  if (props.disabled) {
    event.preventDefault()
    return
  }

  const key = event.key
  if (key === 'Backspace') {
    event.preventDefault()
    if (code.value[index]) {
      // 当前格有值，直接清空
      code.value[index] = ''
      const el = cells.value[index]
      if (el) {
        el.innerText = ''
        nextTick(() => placeCaretAtEnd(el))
      }
    } else if (index > 0) {
      // 当前格无值，跳到前一格并清空它
      const prev = index - 1
      code.value[prev] = ''
      const pel = cells.value[prev]
      currentIndex.value = prev
      if (pel) {
        pel.innerText = ''
        nextTick(() => {
          pel.focus()
          placeCaretAtEnd(pel)
        })
      }
    }
    return
  }
  if (key === 'ArrowLeft') {
    event.preventDefault()
    if (index > 0) {
      currentIndex.value = index - 1
      nextTick(() => cells.value[index - 1]?.focus())
    }
    return
  }
  if (key === 'ArrowRight') {
    event.preventDefault()
    if (index < codeLength - 1) {
      currentIndex.value = index + 1
      nextTick(() => cells.value[index + 1]?.focus())
    }
    return
  }
  if (key === 'Enter') {
    event.preventDefault()
    checkComplete()
    return
  }
  // 其它按键不阻断，交给 beforeinput/input 做数字过滤
}

// 整串粘贴——从"当前索引"开始顺序填充
const handlePaste = (event: ClipboardEvent) => {
  // 如果禁用状态，阻止粘贴
  if (props.disabled) {
    event.preventDefault()
    return
  }

  // 密码模式下禁用粘贴（无论是否显示明文）
  if (props.isPassword) {
    event.preventDefault()
    return
  }

  // 检查是否已经填满，如果填满了则阻止粘贴
  const isFull = code.value.every((char) => char !== '')
  if (isFull) {
    event.preventDefault()
    return
  }

  event.preventDefault()
  const clip = event.clipboardData || (window as any).clipboardData
  const text = clip ? clip.getData('text') : ''
  const numbers = (text || '').replace(/\D/g, '')
  if (!numbers) return
  fillFrom(currentIndex.value, numbers)
}

// 仅在密码模式时拦截复制/剪切/右键菜单，避免侧信道泄露
const onBlockIfPassword = (e: Event) => {
  if (props.isPassword) e.preventDefault()
}

// 把一串数字从指定位置起依次填入，并把焦点移到最后一位
const fillFrom = (start: number, numbers: string) => {
  const arr = numbers.split('').slice(0, codeLength - start)
  for (let i = 0; i < arr.length; i++) {
    const at = start + i
    code.value[at] = arr[i]
  }
  nextTick(() => {
    syncCellsFromCode()

    // 找到第一个空位置
    const firstEmptyIndex = code.value.findIndex((char) => char === '')
    const targetIndex = firstEmptyIndex === -1 ? codeLength - 1 : firstEmptyIndex

    currentIndex.value = targetIndex
    const el = cells.value[currentIndex.value]
    if (el) {
      el.focus()
      placeCaretAtEnd(el)
    }
    checkComplete()
  })
}

// 聚焦时永远选择最前一个未输入的input
const handleFocus = (index: number) => {
  // 如果禁用状态，阻止聚焦
  if (props.disabled) {
    return
  }

  // 找到第一个空的格子
  const firstEmptyIndex = code.value.findIndex((char) => char === '')

  // 如果所有格子都填满了，就选择最后一个格子
  const targetIndex = firstEmptyIndex === -1 ? codeLength - 1 : firstEmptyIndex

  // 如果目标索引与当前索引不同，则跳转到目标索引
  if (targetIndex !== index) {
    currentIndex.value = targetIndex
    const targetEl = cells.value[targetIndex]
    if (targetEl) {
      nextTick(() => {
        targetEl.focus()
        placeCaretAtEnd(targetEl)
      })
    }
  } else {
    // 如果目标索引就是当前索引，直接设置光标位置
    currentIndex.value = index
    const el = cells.value[index]
    if (el) nextTick(() => placeCaretAtEnd(el))
  }
}

const checkComplete = () => {
  const full = code.value.join('')
  if (full.length === codeLength && full.replace(/\D/g, '').length === codeLength) {
    emit('submit', full)
    emit('update:modelValue', full) // 若不想每次完成才同步，可移除此行
    blurAllCells() // ★ 新增：输入满位后立刻失焦
  }
}

// 暴露方法和状态给外部，用于自定义按钮控制
defineExpose({
  toggleVisibility,
  showPassword,
})
</script>

<style scoped>
.otp-container {
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  position: relative;
}

.position-bottom {
  padding-bottom: 42px;
}

.otp-cell {
  flex: 1;
  height: 42px;
  border: 1px solid var(--rim-300);
  border-radius: 10px;
  font-size: 24px;
  text-align: center;
  line-height: 42px;
  caret-color: currentColor;
  outline: none;
  white-space: pre;
}

/* 遮罩时禁用选中，兼容 WebKit 的文字安全（双保险） */
.otp-cell--password {
  -webkit-text-security: disc;
  user-select: none;
}

.otp-cell:focus {
  border-color: var(--primary-300);
}

/* 禁用状态样式 */
.otp-cell--disabled {
  background-color: var(--rim-100, #f5f5f5);
  color: var(--rim-400, #999);
  cursor: not-allowed !important;
  pointer-events: none;
}

.otp-cell--disabled:focus {
  border-color: var(--rim-300);
  outline: none;
}
.otp-cell.focus-white:focus {
  border-color: var(--rim-700);
}
/* 小眼睛按钮样式（可按你的设计系统替换） */
.otp-eye-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  cursor: pointer;
  color: inherit;
}

.right {
  right: -42px;
}

.bottom {
  left: 0;
  bottom: 0;
}

.otp-eye-btn:hover {
  border-color: var(--primary-300);
}

.otp-eye-btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.otp-eye-btn--disabled:hover {
  border-color: var(--rim-300);
}
</style>
